import { useState, useEffect } from 'react';
import { NewsPost, Example } from '../types/content';
import { loadNewsPostsWithEarlyReturn } from '../lib/streamingLoader';
import { loadManifest, loadExamples } from '../lib/contentLoader';

/**
 * Hook for ultra-fast streaming content loading
 * Shows first 3 posts immediately, loads rest progressively
 */
export function useStreamingNews() {
  const [initialPosts, setInitialPosts] = useState<NewsPost[]>([]);
  const [allPosts, setAllPosts] = useState<NewsPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStreamingContent();
  }, []);

  const loadStreamingContent = async () => {
    try {
      setError(null);
      
      // Get file list
      const manifest = await loadManifest();
      
      // Load with early return pattern for instant display
      const { initialPosts: early, remainingPromise } = await loadNewsPostsWithEarlyReturn(manifest.news);
      
      // Show first posts immediately
      setInitialPosts(early);
      setAllPosts(early);
      setInitialLoading(false);
      
      // Load remaining posts in background
      const allPosts = await remainingPromise;
      setAllPosts(allPosts);
      setLoading(false);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load content');
      setLoading(false);
      setInitialLoading(false);
    }
  };

  return {
    posts: allPosts,
    initialPosts,
    loading,
    initialLoading,
    error,
    reload: loadStreamingContent
  };
}

/**
 * Hook for immediate content display with progressive enhancement
 */
export function useInstantContent() {
  const [posts, setPosts] = useState<NewsPost[]>([]);
  const [examples, setExamples] = useState<Example[]>([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [examplesLoading, setExamplesLoading] = useState(true);
  const [hasInitialContent, setHasInitialContent] = useState(false);

  useEffect(() => {
    loadInstantContent();
  }, []);

  const loadInstantContent = async () => {
    try {
      // Load manifest first
      const manifest = await loadManifest();
      
      // Start both news and examples loading simultaneously
      const [newsPromise, examplesPromise] = await Promise.allSettled([
        loadNewsPostsWithEarlyReturn(manifest.news),
        loadExamples()
      ]);
      
      // Handle news results
      if (newsPromise.status === 'fulfilled') {
        const { initialPosts, remainingPromise } = newsPromise.value;
        
        // Display initial content immediately
        setPosts(initialPosts);
        setHasInitialContent(true);
        setPostsLoading(false);
        
        // Load remaining content in background
        remainingPromise.then((allPosts) => {
          setPosts(allPosts);
        });
      } else {
        setPostsLoading(false);
      }

      // Handle examples results
      if (examplesPromise.status === 'fulfilled') {
        setExamples(examplesPromise.value);
      }
      setExamplesLoading(false);
      
    } catch (error) {
      console.error('Error in instant content loading:', error);
      setPostsLoading(false);
      setExamplesLoading(false);
    }
  };

  return {
    posts,
    examples,
    loading: postsLoading || examplesLoading,
    postsLoading,
    examplesLoading,
    hasInitialContent,
    reload: loadInstantContent
  };
}